import React, { useEffect, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import "./ReviewerReviewPaper.css";

const ReviewPapers = () => {
  const [papers, setPapers] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [selectedPaper, setSelectedPaper] = useState(null);
  const [reviewMessage, setReviewMessage] = useState("");
  const [file, setFile] = useState(null);
  const [recommendation, setRecommendation] = useState("");
  const [noPapersMessage, setNoPapersMessage] = useState("");
  const [currentReview, setCurrentReview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const papersPerPage = 10;

  // Function to fetch papers
  const fetchPapers = async () => {
    const email = localStorage.getItem("email");
    if (!email) {
      setError("No email found. Please log in.");
      return;
    }
    setLoading(true);
    const apiUrl = `${process.env.REACT_APP_BACKEND_SERVER_URL}/api/v1/reviewer/review-papers?email=${encodeURIComponent(email)}`;
    try {
      const response = await axios.get(apiUrl);
      if (response.data.message) {
        setNoPapersMessage(response.data.message);
      } else {
        setPapers(response.data);
      }
      setLoading(false);
    } catch (error) {
      console.error("Error fetching papers:", error);
      setError("Failed to fetch papers. Please try again later.");
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPapers();
  }, []);

  // Debug: Log papers when they change
  useEffect(() => {
    if (papers.length > 0) {
      console.log("Papers data:", papers);
      console.log("First paper domains:", papers[0].paperDomains);
    }
  }, [papers]);

  const handleReviewClick = (paper) => {
    setSelectedPaper(paper);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedPaper(null);
    setReviewMessage("");
    setFile(null);
    setRecommendation("");
  };

  const handleFileChange = (event) => {
    setFile(event.target.files[0]);
  };

  const handleSubmitReview = async () => {
    if (!reviewMessage.trim() || !recommendation) {
      Swal.fire({
        title: "Error",
        text: "Please provide a review message and recommendation.",
        icon: "error",
        customClass: {
          container: 'swal-high-z-index'
        }
      });
      return;
    }

    // Confirmation alert
    const result = await Swal.fire({
      title: "Confirm Submission",
      text: "Are you sure you want to submit this review? This action cannot be undone.",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#5e71f6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, Submit Review",
      cancelButtonText: "Cancel",
      customClass: {
        container: 'swal-high-z-index'
      }
    });

    if (!result.isConfirmed) {
      return;
    }

    setSubmitting(true);

    const formData = new FormData();
    formData.append("reviewMessage", reviewMessage);
    formData.append("reviewDate", new Date().toISOString());
    formData.append("paperId", selectedPaper.id);
    formData.append("reviewerEmail", localStorage.getItem("email"));
    formData.append("recommendation", recommendation);
    if (file) {
      formData.append("file", file);
    }

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_SERVER_URL}/api/v1/reviewer/submit-review`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );
      Swal.fire({
        title: "Success",
        text: "Reviewed the paper successfully",
        icon: "success",
        customClass: {
          container: 'swal-high-z-index'
        }
      });
      handleCloseModal();
      fetchPapers();
    } catch (error) {
      console.error("Error submitting review:", error);
      Swal.fire({
        title: "Error",
        text: "Failed to submit review. Please try again.",
        icon: "error",
        customClass: {
          container: 'swal-high-z-index'
        }
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleShowReview = (paper) => {
    setSelectedPaper(paper);
    setShowReviewModal(true);
  };

  const handleCloseReviewModal = () => {
    setShowReviewModal(false);
    setCurrentReview(null);
    setSelectedPaper(null);
  };

  return (
    <div className="review-papers-wrapper">
      <div className="review-papers-container">
        {/* Header Section */}
        <div className="header-section">
          <h1 className="page-title">Review Papers</h1>
          <p className="page-subtitle">Manage and review all assigned papers</p>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading assigned papers...</p>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="error-container">
            <div className="error-message">
              <span className="error-icon">⚠️</span>
              <span>{error}</span>
            </div>
          </div>
        )}

        {/* No Papers Message */}
        {noPapersMessage && !loading && !error && (
          <div className="no-papers-container">
            <svg className="no-papers-icon" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p>{noPapersMessage}</p>
          </div>
        )}

        {/* Papers List */}
        {!loading && !error && !noPapersMessage && papers.length > 0 && (() => {
          // Pagination logic
          const indexOfLastPaper = currentPage * papersPerPage;
          const indexOfFirstPaper = indexOfLastPaper - papersPerPage;
          const currentPapers = papers.slice(indexOfFirstPaper, indexOfLastPaper);
          const totalPages = Math.ceil(papers.length / papersPerPage);

          return (
            <>
              <div className="papers-grid">
                {currentPapers.map((paper) => {
              const reviewerWithReview = paper.assignedReviewer?.find(
                (reviewer) =>
                  reviewer.email === localStorage.getItem("email") &&
                  (reviewer.reviewInfo?.reviewMessage ||
                    reviewer.reviewInfo?.reviewPicURL)
              );

              // Find reviewer assignment info
              const assignedReviewer = paper.assignedReviewer?.find(
                (reviewer) => reviewer.email === localStorage.getItem("email")
              );

              return (
                <div key={paper.id} className="paper-card">
                  <div className="paper-card-header">
                    <span className="paper-id-badge">Paper ID: {paper.paperID || paper.id}</span>
                    {reviewerWithReview && (
                      <span className="status-badge reviewed">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        Reviewed
                      </span>
                    )}
                  </div>
                  
                  <div className="paper-card-body">
                    <h3 className="paper-title">{paper.title}</h3>
                    
                    <div className="paper-details">
                      {assignedReviewer && (
                        <div className="detail-row">
                          <span className="detail-label">Assigned Date:</span>
                          <span className="detail-value">
                            {new Date(paper.createdAt || Date.now()).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            })}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="paper-card-footer">
                    <button
                      className="action-btn view-paper-btn"
                    onClick={() => {
                      if (paper.paperURL || paper.cloudinaryURL) {
                        window.open(paper.paperURL || paper.cloudinaryURL, "_blank", "noopener,noreferrer");
                      } else {
                        Swal.fire({
                          title: "Error",
                          text: "Paper URL is not available",
                          icon: "error",
                          customClass: {
                            container: 'swal-high-z-index'
                          }
                        });
                      }
                    }}
                    >
                      <svg className="btn-icon" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                      View Paper
                    </button>

                    {reviewerWithReview ? (
                      <button
                        className="action-btn review-info-btn"
                        onClick={() => handleShowReview(paper)}
                      >
                        <svg className="btn-icon" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        View My Review
                      </button>
                    ) : (
                      <button
                        className="action-btn submit-review-btn"
                        onClick={() => handleReviewClick(paper)}
                      >
                        <svg className="btn-icon" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        Write Review
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="pagination">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="pagination-nav-button"
              >
                <svg
                  className="pagination-icon"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
                Previous
              </button>
              
              <div className="pagination-numbers">
                {Array.from({ length: totalPages }, (_, index) => {
                  const pageNum = index + 1;
                  if (
                    pageNum === 1 ||
                    pageNum === totalPages ||
                    (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                  ) {
                    return (
                      <button
                        key={index}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`pagination-number ${
                          currentPage === pageNum ? "active" : ""
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  } else if (
                    pageNum === currentPage - 2 ||
                    pageNum === currentPage + 2
                  ) {
                    return <span key={index} className="pagination-ellipsis">...</span>;
                  }
                  return null;
                })}
              </div>

              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="pagination-nav-button"
              >
                Next
                <svg
                  className="pagination-icon"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
            </div>
          )}
        </>
        );
        })()}

        {/* Empty State */}
        {!loading && !error && !noPapersMessage && papers.length === 0 && (
          <div className="no-papers-container">
            <svg className="no-papers-icon" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p>No papers assigned for review</p>
          </div>
        )}
      </div>

      {/* Modal for Submitting Review */}
      {showModal && selectedPaper && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Submit Review</h2>
              <button className="modal-close-btn" onClick={handleCloseModal}>
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="modal-body">
              <div className="paper-info-modal">
                <span className="paper-id-badge">Paper ID: {selectedPaper.paperID || selectedPaper.id}</span>
                <h3 className="modal-paper-title">{selectedPaper.title}</h3>
              </div>

              <div className="form-group">
                <label htmlFor="recommendation" className="form-label">
                  Recommendation <span className="required">*</span>
                </label>
                <select
                  id="recommendation"
                  value={recommendation}
                  onChange={(e) => setRecommendation(e.target.value)}
                  className="form-select"
                  required
                >
                  <option value="">Select your recommendation</option>
                  <option value="Accept">✓ Accept</option>
                  <option value="Reject">✗ Reject</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="reviewMessage" className="form-label">
                  Review Comments <span className="required">*</span>
                </label>
                <textarea
                  id="reviewMessage"
                  className="form-textarea"
                  value={reviewMessage}
                  onChange={(e) => setReviewMessage(e.target.value)}
                  placeholder="Provide detailed feedback on the paper's strengths, weaknesses, and suggestions for improvement..."
                  rows="8"
                  required
                />
                <span className="character-count">{reviewMessage.length} characters</span>
              </div>

              <div className="form-group">
                <label htmlFor="fileUpload" className="form-label">
                  Attach Review Document (Optional)
                  <span className="file-info">PDF, DOC, DOCX (Max 10MB)</span>
                </label>
                <div className="file-upload-card">
                  <input
                    type="file"
                    id="fileUpload"
                    className="file-input"
                    onChange={handleFileChange}
                    accept=".pdf,.doc,.docx"
                  />
                  <label htmlFor="fileUpload" className="file-upload-label">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    <span className="upload-text">{file ? file.name : "Choose file or drag here"}</span>
                    {!file && <span className="upload-hint">PDF, DOC, DOCX (MAX. 10MB)</span>}
                  </label>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button 
                className="modal-btn cancel-btn" 
                onClick={handleCloseModal}
                disabled={submitting}
              >
                Cancel
              </button>
              <button 
                className="modal-btn submit-btn" 
                onClick={handleSubmitReview}
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <svg className="btn-spinner" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Submitting...
                  </>
                ) : (
                  <>
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Submit Review
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal for Showing Review */}
      {showReviewModal && selectedPaper && (
        <div className="modal-overlay" onClick={handleCloseReviewModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Your Review</h2>
              <button className="modal-close-btn" onClick={handleCloseReviewModal}>
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="modal-body">
              <div className="paper-info-modal">
                <span className="paper-id-badge">Paper ID: {selectedPaper.paperID || selectedPaper.id}</span>
                <h3 className="modal-paper-title">{selectedPaper.title}</h3>
              </div>

              <div className="review-display">
                {(() => {
                  const reviewerWithReview = selectedPaper.assignedReviewer?.find(
                    (reviewer) => reviewer.email === localStorage.getItem("email")
                  );
                  const reviewInfo = reviewerWithReview?.reviewInfo;

                  return (
                    <>
                      {reviewInfo?.recommendation && (
                        <div className="review-section">
                          <h4 className="review-section-title">Recommendation</h4>
                          <div className={`recommendation-badge ${reviewInfo.recommendation.toLowerCase()}`}>
                            {reviewInfo.recommendation === "Accept" ? "✓" : "✗"} {reviewInfo.recommendation}
                          </div>
                        </div>
                      )}

                      <div className="review-section">
                        <h4 className="review-section-title">Review Comments</h4>
                        <div className="review-text-box">
                          <p className="review-text">
                            {reviewInfo?.reviewMessage || "No review message provided"}
                          </p>
                        </div>
                      </div>

                      {reviewInfo?.reviewPicURL && (
                        <div className="review-section">
                          <h4 className="review-section-title">Attached Document</h4>
                          <a
                            href={reviewInfo.reviewPicURL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="review-document-link"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            View Review Document
                          </a>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>

            <div className="modal-footer">
              <button className="modal-btn cancel-btn" onClick={handleCloseReviewModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReviewPapers;
